import Button from "components/button/Button";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaArrowRight,
  FaSave,
  FaExclamationTriangle,
  FaCheckCircle,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useStepNavigation } from "hooks/useStepNavigation";

interface Props {
  step: number;
  totalSteps: number;
  setStep: (step: number) => void;
  loading?: boolean;
  onSubmit?: () => void;
  mode?: string;
  hideSubmit?: boolean;
  isTopNav?: boolean;
}

export default function StepNavigation({
  step,
  totalSteps,
  setStep,
  loading,
  onSubmit,
  mode,
  hideSubmit,
  isTopNav = false,
}: Props) {
  const isLast = step === totalSteps;
  const isFirst = step === 1;
  const isCreateMode = mode === "create";
  const isViewMode = mode === "view";

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "fa" || i18n.language === "ar";

  const {
    isErrorsBoxOpen,
    completedSteps,
    errorRef,
    currentStepErrors,
    errorCount,
    nextStep,
    prevStep,
  } = useStepNavigation(step, setStep, isViewMode);

  const isCurrentStepCompleted = completedSteps.has(step) && errorCount === 0;

  /**
   * Navigation button styles based on state
   */
  const getNavStyles = () => {
    if (isErrorsBoxOpen && errorCount > 0) {
      return "shadow-lg shadow-red-500/10 ring-2 ring-red-400/30 dark:shadow-red-500/5 dark:ring-red-500/30";
    }
    if (isCurrentStepCompleted && !isLast) {
      return "shadow-lg shadow-green-500/10 ring-2 ring-green-400/30 dark:shadow-green-500/5 dark:ring-green-500/30";
    }
    return "shadow-lg shadow-indigo-500/10 ring-1 ring-white/10 dark:bg-gray-800/40 dark:shadow-indigo-500/5 dark:ring-white/5";
  };

  /**
   * Next button styles based on state
   */
  const getNextButtonStyles = () => {
    if (isErrorsBoxOpen && errorCount > 0) {
      return "dark:bg-red-950/40 border-red-400 bg-red-50 text-red-600 hover:bg-red-100 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-900/50";
    }
    if (isCurrentStepCompleted && !isLast) {
      return "dark:bg-green-950/40 border-green-400 bg-green-50 text-green-600 hover:bg-green-100 dark:border-green-500/40 dark:text-green-400 dark:hover:bg-green-900/50";
    }
    return "dark:bg-indigo-950/30 border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:border-indigo-500/30 dark:text-indigo-400 dark:hover:bg-indigo-900/40";
  };

  // View mode
  if (isViewMode) {
    return (
      <div className="space-y-3">
        <div
          className={`flex flex-col items-center justify-between gap-3 rounded-xl bg-indigo-50/80 p-3 backdrop-blur-sm transition-all duration-300 dark:bg-gray-800/60 sm:flex-row ${
            isRTL ? "flex-row-reverse" : ""
          }`}
        >
          <div className="order-2 flex items-center gap-2 sm:order-1">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="outline"
                size="sm"
                onClick={prevStep}
                disabled={isFirst}
                startIcon={
                  !isRTL ? (
                    <FaArrowLeft size={12} />
                  ) : (
                    <FaArrowRight size={12} />
                  )
                }
                className="px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("buttons.prevStep")}
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={nextStep}
                disabled={isLast}
                endIcon={
                  !isRTL ? (
                    <FaArrowRight size={12} />
                  ) : (
                    <FaArrowLeft size={12} />
                  )
                }
                className="dark:bg-indigo-950/30 border-indigo-200 bg-indigo-50 px-4 py-1.5 text-sm font-medium text-indigo-600 shadow-md transition-all duration-200 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-500/30 dark:text-indigo-400 dark:hover:bg-indigo-900/40"
              >
                {t("buttons.nextStep")}
              </Button>
            </motion.div>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t("company.processSteps.step", {
              current: step,
              total: totalSteps,
            })}
          </div>
        </div>
      </div>
    );
  }

  // Top navigation with error box
  if (isTopNav) {
    return (
      <div className="space-y-3" id="error-box-container" ref={errorRef}>
        {/* Error box - always visible when errors exist */}
        {isErrorsBoxOpen && errorCount > 0 && (
          <motion.div
            key={`errors-top-${step}`}
            initial={{ opacity: 0, y: -20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="dark:bg-red-950/40 overflow-hidden rounded-lg border border-red-200 bg-red-50 p-4 shadow-lg shadow-red-500/10 dark:border-red-800/50 dark:shadow-red-500/5"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-red-100 p-1.5 dark:bg-red-900/50">
                <FaExclamationTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-red-800 dark:text-red-200">
                    {t("validation.validationErrors")}
                  </p>
                  <span className="text-xs text-red-600 dark:text-red-400">
                    {errorCount} {t("validation.error")}
                  </span>
                </div>
                <p className="mb-2 text-xs text-red-600 dark:text-red-300">
                  {t("validation.fixErrors")}
                </p>
                <ul className="space-y-1">
                  {currentStepErrors.slice(0, 5).map((error, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-red-500 dark:bg-red-400" />
                      <span>{error}</span>
                    </motion.li>
                  ))}
                  {currentStepErrors.length > 5 && (
                    <li className="text-red-500 dark:text-red-400">
                      {t("validation.andMoreErrors", {
                        count: currentStepErrors.length - 5,
                      })}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step completion message */}
        <AnimatePresence>
          {isCurrentStepCompleted && !isLast && !isErrorsBoxOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="dark:bg-green-950/40 rounded-lg border border-green-200 bg-green-50 p-3 shadow-lg shadow-green-500/10 dark:border-green-800/50 dark:shadow-green-500/5"
            >
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-green-600 dark:text-green-400" />
                <p className="text-sm text-green-800 dark:text-green-200">
                  {t("validation.stepComplete")}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation buttons */}
        <div
          className={`flex flex-col items-center justify-between gap-3 rounded-xl bg-indigo-50/80 p-3 backdrop-blur-sm transition-all duration-300 dark:bg-gray-800/60 sm:flex-row ${
            isRTL ? "flex-row-reverse" : ""
          } ${getNavStyles()}`}
        >
          <div className="order-2 flex items-center gap-2 sm:order-1">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                variant="outline"
                size="sm"
                onClick={prevStep}
                disabled={isFirst}
                startIcon={
                  !isRTL ? (
                    <FaArrowLeft size={12} />
                  ) : (
                    <FaArrowRight size={12} />
                  )
                }
                className="px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("buttons.prevStep")}
              </Button>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              animate={
                errorCount > 0 && isErrorsBoxOpen
                  ? { x: [-2, 2, -2, 2, 0] }
                  : {}
              }
              transition={{ duration: 0.3 }}
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={nextStep}
                disabled={isLast}
                endIcon={
                  !isRTL ? (
                    <FaArrowRight size={12} />
                  ) : (
                    <FaArrowLeft size={12} />
                  )
                }
                className={`px-4 py-1.5 text-sm font-medium shadow-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${getNextButtonStyles()}`}
              >
                {t("buttons.nextStep")}
                {errorCount > 0 && isErrorsBoxOpen && ` (${errorCount})`}
              </Button>
            </motion.div>
          </div>

          {!hideSubmit && (
            <div className="order-1 flex items-center sm:order-2">
              <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="w-full sm:w-auto"
              >
                <Button
                  onClick={onSubmit}
                  variant="primary"
                  size="sm"
                  loading={loading}
                  disabled={isCreateMode && !isLast}
                  startIcon={<FaSave size={12} />}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30 transition-all duration-300 hover:shadow-indigo-500/40 dark:from-indigo-500 dark:to-purple-500 dark:shadow-indigo-500/20 dark:hover:shadow-indigo-500/30 sm:w-auto"
                >
                  {isCreateMode
                    ? t("company.companySubmit")
                    : t("company.companyEdit")}
                </Button>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Bottom navigation - buttons only
  return (
    <div className="space-y-3">
      <div
        className={`flex flex-col items-center justify-between gap-3 rounded-xl bg-indigo-50/80 p-3 backdrop-blur-sm transition-all duration-300 dark:bg-gray-800/60 sm:flex-row ${
          isRTL ? "flex-row-reverse" : ""
        } ${getNavStyles()}`}
      >
        <div className="order-2 flex items-center gap-2 sm:order-1">
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              variant="outline"
              size="sm"
              onClick={prevStep}
              disabled={isFirst}
              startIcon={
                !isRTL ? <FaArrowLeft size={12} /> : <FaArrowRight size={12} />
              }
              className="px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("buttons.prevStep")}
            </Button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            animate={
              errorCount > 0 && isErrorsBoxOpen ? { x: [-2, 2, -2, 2, 0] } : {}
            }
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={nextStep}
              disabled={isLast}
              endIcon={
                !isRTL ? <FaArrowRight size={12} /> : <FaArrowLeft size={12} />
              }
              className={`px-4 py-1.5 text-sm font-medium shadow-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${getNextButtonStyles()}`}
            >
              {t("buttons.nextStep")}
              {errorCount > 0 && isErrorsBoxOpen && ` (${errorCount})`}
            </Button>
          </motion.div>
        </div>

        {!hideSubmit && (
          <div className="order-1 flex items-center sm:order-2">
            <motion.div
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full sm:w-auto"
            >
              <Button
                onClick={onSubmit}
                variant="primary"
                size="sm"
                loading={loading}
                disabled={isCreateMode && !isLast}
                startIcon={<FaSave size={12} />}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30 transition-all duration-300 hover:shadow-indigo-500/40 dark:from-indigo-500 dark:to-purple-500 dark:shadow-indigo-500/20 dark:hover:shadow-indigo-500/30 sm:w-auto"
              >
                {isCreateMode
                  ? t("company.companySubmit")
                  : t("company.companyEdit")}
              </Button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
